import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { SafeUserShape } from '@suangua/shared';
import { fetchMe, logout as apiLogout } from '@suangua/shared';

/**
 * 认证上下文（双端共用逻辑）
 * - 挂载时请求 /api/auth/me 恢复登录态（Cookie 会话）
 * - 提供 setUser / refresh / logout 供各页面使用
 */
export interface AuthContextValue {
  /** 当前登录用户；null=未登录 */
  user: SafeUserShape | null;
  /** 首次恢复登录态进行中（避免闪烁） */
  loading: boolean;
  /** 手动刷新用户信息（如资料修改后） */
  refresh: () => Promise<void>;
  /** 更新本地用户状态（登录成功后调用） */
  setUser: (user: SafeUserShape | null) => void;
  /** 登出：调用 API 并清空本地态 */
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SafeUserShape | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await fetchMe();
      setUser(res.user);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } finally {
      setUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({ user, loading, refresh, setUser, logout }),
    [user, loading, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** 使用认证上下文（必须在 AuthProvider 内） */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth 必须在 AuthProvider 内使用');
  return ctx;
}